// Portal session: branding from the public config, the signed-in partner user and their firm.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  get,
  type Me,
  onSessionChange,
  type PortalConfig,
  refreshSession,
  setSession,
} from './api.js';

interface SessionValue {
  config: PortalConfig | null;
  configError: boolean;
  /** 'loading' until the first refresh attempt has finished. */
  state: 'loading' | 'out' | 'in';
  me: Me | null;
  isOwner: boolean;
  approved: boolean;
  /** Stores the tokens answered by sign-in, apply, accept and reset. */
  signedIn: (r: { accessToken: string; expiresIn: number }) => void;
  signOut: () => Promise<void>;
  reloadMe: () => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** The firm's primary colour (validated #rrggbb) replaces the theme's. */
function applyBranding(config: PortalConfig | null): void {
  const root = document.documentElement;
  const colour = config?.primaryColour;
  if (colour && /^#[0-9a-f]{6}$/i.test(colour)) {
    root.style.setProperty('--primary', colour);
    root.style.setProperty('--primary-hover', colour);
  }
  if (config?.brandName) document.title = `${config.brandName} partner portal`;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = useState<'loading' | 'out' | 'in'>('loading');
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () => get<PortalConfig>('/config'),
    staleTime: 300_000,
  });
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/me'),
    enabled: state === 'in',
  });

  useEffect(() => applyBranding(cfg.data ?? null), [cfg.data]);

  // A token the server no longer accepts (account switched off, firm suspended): back to sign-in.
  const meStatus = meQuery.error instanceof ApiError ? meQuery.error.status : 0;
  useEffect(() => {
    if (meStatus === 401 || meStatus === 403) setSession(null);
  }, [meStatus]);

  useEffect(() => {
    let live = true;
    void refreshSession().then((ok) => live && setState(ok ? 'in' : 'out'));
    const off = onSessionChange((signedIn) => {
      if (!signedIn) {
        qc.clear();
        setState('out');
      }
    });
    return () => {
      live = false;
      off();
    };
  }, [qc]);

  const signedIn = useCallback((r: { accessToken: string; expiresIn: number }) => {
    setSession(r.accessToken, r.expiresIn);
    setState('in');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('POST', '/auth/logout');
    } finally {
      setSession(null);
    }
  }, []);

  const value = useMemo<SessionValue>(() => {
    const me = meQuery.data ?? null;
    return {
      config: cfg.data ?? null,
      configError: cfg.isError,
      state,
      me,
      isOwner: me?.me.role === 'OWNER',
      approved: me?.status === 'APPROVED',
      signedIn,
      signOut,
      reloadMe: () => void qc.invalidateQueries({ queryKey: ['me'] }),
    };
  }, [cfg.data, cfg.isError, state, meQuery.data, signedIn, signOut, qc]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
